import {RequestInfo, type UserAttributes} from "@khan/shared-core";
import {getClassroomUrl} from "@khan/urls";

/**
 * Determines if a classroom should redirect to Khan Classroom
 */
export const shouldRedirectToKaClassroom = (
    isKhanClassroom?: boolean,
): boolean => {
    // Only redirect if this is a Khan Classroom
    if (!isKhanClassroom) {
        return false;
    }

    // Don't redirect in embedded contexts.
    // Some embedded contexts include:
    // - Canvas LTI integration (Khanmigo Teacher Tools)
    // - Khan Classroom embedding classic KA content
    // - Mobile app webviews
    return !RequestInfo.embedded;
};

/**
 * Gets the Polaris redirect URL. If no signupCode is provided, returns the base URL.
 */
export const getPolarisRedirectUrl = (
    signupCode?: string,
    userAttributes?: UserAttributes | null,
): string => {
    if (!signupCode) {
        return `${getClassroomUrl()}/`;
    }
    // isTeacher is set if a user has the teacher role set in their user
    // settings. But there are other definitions of a teacher, e.g. K4D
    // teacher and "formal teacher". Users with these definitions may not
    // have isTeacher set, so we check hasClasses also as a stop gap.
    //
    // See the following pages for more context:
    // https://khanacademy.atlassian.net/wiki/spaces/~5a8c69abee53fc2116351fe9/pages/4359454857/Bug+Journal+-+District+admin+redirected+to+learner+experience
    // https://khanacademy.atlassian.net/wiki/spaces/CLASS/pages/2655256657/Product+Definitions+of+Teacher
    const isTeacher = userAttributes?.isTeacher || userAttributes?.hasClasses;

    const role = isTeacher ? "teacher" : "learner";
    return `${getClassroomUrl()}/${role}/class/${signupCode}`;
};

/**
 * Gets the Polaris redirect URL for a specific tool.
 * Always goes to teacher tools since tools are teacher-only in classroom.
 */
export const getPolarisToolRedirectUrl = (toolSlug: string): string => {
    return `${getClassroomUrl()}/teacher/tools/${toolSlug}`;
};
